import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import MainContainer from "../../../layout/MainContainer";
import WarnModal from "../../../components/WarnModal";
import { showError, showToast } from "../../../constants";
import { AddNews, GetNewsById, UpdateNews, UpdateNewsStatus, DeleteNews } from "../../../services/home";
import { News } from "../../../types/General";

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required").max(50, "Maximum 50 characters allowed"),
  description: Yup.string().required("Description is required").max(256, "Maximum 256 characters allowed"),
});

const NewsFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [existingNews, setExistingNews] = useState<News | null>(
    (location.state as any)?.item || null
  );
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (isEdit && !existingNews) {
      loadNews();
    }
  }, [id]);

  const loadNews = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = (await GetNewsById(id)) as { status: number; data: News };
      if (response?.status === 200) {
        setExistingNews(response.data);
      } else {
        showError("News not found");
        navigate("/manage-news");
      }
    } catch (error) {
      showError("Failed to load news");
      navigate("/manage-news");
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: {
      title: existingNews?.title ?? "",
      description: existingNews?.description ?? "",
    },
    validationSchema,
    onSubmit: () => {},
  });

  const handleSubmit = async (status: string) => {
    const errors = await formik.validateForm();
    formik.setTouched({ title: true, description: true });

    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      if (isEdit && id) {
        const response = (await UpdateNews(id, {
          title: formik.values.title,
          description: formik.values.description,
          status,
        })) as { status: number };
        if (response?.status === 200) {
          showToast("News updated successfully");
          navigate("/manage-news");
        }
      } else {
        const response = (await AddNews({
          title: formik.values.title,
          description: formik.values.description,
          status,
        })) as { status: number };
        if (response?.status === 201) {
          showToast("News created successfully");
          navigate("/manage-news");
        }
      }
    } catch (error) {
      showError("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      const response = (await UpdateNewsStatus(id, { status: "Disabled" })) as { status: number };
      if (response?.status === 200) {
        showToast("News disabled successfully");
        navigate("/manage-news");
      }
    } catch (error) {
      showError("Failed to disable news");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      const response = (await DeleteNews(id)) as { status: number };
      if (response?.status === 200) {
        showToast("News deleted successfully");
        navigate("/manage-news");
      }
    } catch (error) {
      showError("Failed to delete news");
    }
  };

  if (loading) {
    return (
      <MainContainer>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  const currentStatus = existingNews?.status;

  return (
    <MainContainer>
      <Box sx={{ maxWidth: 700, mx: "auto" }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
          {isEdit ? "Edit News" : "Add News"}
        </Typography>

        <Paper sx={{ p: 4, boxShadow: "none", border: "1px solid #e0e0e0" }}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Title <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="title"
              placeholder="Enter news title..."
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={
                formik.touched.title && formik.errors.title
                  ? formik.errors.title
                  : `${formik.values.title.length}/50`
              }
              inputProps={{ maxLength: 50 }}
              size="small"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Description <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              name="description"
              placeholder="Enter brief description..."
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={
                formik.touched.description && formik.errors.description
                  ? formik.errors.description
                  : `${formik.values.description.length}/256`
              }
              inputProps={{ maxLength: 256 }}
              size="small"
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
            {!isEdit && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => handleSubmit("Draft")}
                  disabled={submitting}
                >
                  SAVE AS DRAFT
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleSubmit("Published")}
                  disabled={submitting}
                >
                  PUBLISH
                </Button>
              </>
            )}

            {isEdit && currentStatus === "Draft" && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteOpen(true)}
                  disabled={submitting}
                >
                  DISCARD
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleSubmit("Draft")}
                  disabled={submitting}
                >
                  SAVE AS DRAFT
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleSubmit("Published")}
                  disabled={submitting}
                >
                  PUBLISH
                </Button>
              </>
            )}

            {isEdit && currentStatus === "Published" && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDisable}
                  disabled={submitting}
                >
                  DISABLE
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleSubmit("Published")}
                  disabled={submitting}
                >
                  UPDATE
                </Button>
              </>
            )}

            {isEdit && currentStatus === "Disabled" && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteOpen(true)}
                  disabled={submitting}
                >
                  DELETE
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleSubmit("Disabled")}
                  disabled={submitting}
                >
                  UPDATE
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleSubmit("Published")}
                  disabled={submitting}
                >
                  PUBLISH
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Box>

      <WarnModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        handleDelete={handleDelete}
        name="News"
      />
    </MainContainer>
  );
};

export default NewsFormPage;
